import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
} from 'antd';
import type { FormProps } from 'antd/es/form';
import React from 'react';

import { DUMMY_SERVICE_OPTIONS } from '../../constants';
import { INIT_FORM_VALUES, MEMBER_OPTIONS, PLAN_OPTIONS } from './constants';
import styles from './index.module.scss';
import type { PointDistributionFormValues } from './types';

type AddPointDistributionModalProps = {
  open: boolean;
  onClose: () => void;
};

const { useForm, Item, useWatch } = Form;

export const AddPointDistributionModal = ({
  open,
  onClose,
}: AddPointDistributionModalProps) => {
  const [form] = useForm<PointDistributionFormValues>();
  const { resetFields, setFieldValue } = form;

  const isTopUp = useWatch('isTopUp', form);
  const planRecipients = useWatch(['recipients', 'plans'], form);
  const memberRecipients = useWatch(['recipients', 'members'], form);

  const closeModal = () => {
    resetFields();
    onClose();
  };

  const onFormFinish = (formValues: PointDistributionFormValues) => {
    console.log('formValues:\n', formValues);
  };

  const handleFieldsChange: FormProps['onFieldsChange'] = (changedFields) => {
    const isTopUpField = changedFields.find(
      (field) => field.name[0] === 'isTopUp'
    );

    if (!isTopUpField) return;

    if (isTopUpField.name[0] === 'isTopUp' && isTopUpField.value === false) {
      setFieldValue(['transactionPoints', 'topUp'], null);
      setFieldValue('expiration', null);
    }
    if (isTopUpField.name[0] === 'isTopUp' && isTopUpField.value === true) {
      setFieldValue(['transactionPoints', 'deduction'], null);
    }
  };

  const isRecipientsFieldsAllEmpty =
    planRecipients &&
    memberRecipients &&
    planRecipients.length === 0 &&
    memberRecipients.length === 0;

  return (
    <Modal
      title="新增點數發放"
      centered
      destroyOnClose
      maskClosable={false}
      open={open}
      onCancel={closeModal}
      footer={null}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onFormFinish}
        initialValues={INIT_FORM_VALUES}
        onFieldsChange={handleFieldsChange}
        validateTrigger="onSubmit"
      >
        <Item
          label="服務"
          name="service"
          rules={[{ required: true, message: '請選擇服務' }]}
        >
          <Select placeholder="請選擇服務" options={DUMMY_SERVICE_OPTIONS} />
        </Item>

        <Item
          label="交易名目"
          name="transactionName"
          rules={[{ required: true, message: '請輸入交易名目' }]}
        >
          <Input placeholder="請輸入交易名目" allowClear />
        </Item>

        <Item label="發放對象" required>
          <Space
            direction="vertical"
            className={styles.recipientsFieldContainer}
          >
            <div className={styles.recipientsField}>
              <p>方案對象：</p>
              <Item
                name={['recipients', 'plans']}
                noStyle
                rules={[
                  {
                    required: isRecipientsFieldsAllEmpty,
                    message: '',
                  },
                ]}
              >
                <Select
                  allowClear
                  showArrow
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="請選擇方案對象"
                  // TODO: 假資料，要改成真實資料
                  options={PLAN_OPTIONS}
                />
              </Item>
            </div>
            <div className={styles.recipientsField}>
              <p>會員對象：</p>
              <Item
                name={['recipients', 'members']}
                noStyle
                rules={[
                  {
                    required: isRecipientsFieldsAllEmpty,
                    message: '請至少選擇一個對象',
                  },
                ]}
              >
                <Select
                  allowClear
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="搜尋會員對象"
                  // TODO: 假資料，要改成真實資料，這邊的options，會是用戶搜尋的同時去打API獲取符合條件的會員資料列表
                  options={MEMBER_OPTIONS}
                />
              </Item>
            </div>
          </Space>
        </Item>

        <Item label="交易點數" required>
          <Item
            name="isTopUp"
            rules={[{ required: true, message: '請選擇其一' }]}
          >
            <Radio.Group>
              <Space size="large" align="baseline">
                <Radio value={false}>扣值</Radio>
                <Space align="baseline">
                  <p className={styles['mb-0']}>點數：</p>
                  <Item
                    name={['transactionPoints', 'deduction']}
                    rules={[
                      {
                        required: isTopUp === false,
                        message: '請輸入扣值點數',
                      },
                    ]}
                  >
                    <InputNumber
                      className={styles.inputWidth}
                      placeholder="例如：10（不需輸入單位）"
                      min={1}
                      step={1}
                    />
                  </Item>
                </Space>
              </Space>
              <Space direction="vertical">
                <Space size="large" align="baseline">
                  <Radio value={true}>加值</Radio>
                  <Space align="baseline">
                    <p className={styles['mb-0']}>點數：</p>
                    <Item
                      name={['transactionPoints', 'topUp']}
                      rules={[
                        {
                          required: isTopUp === true,
                          message: '請輸入加值點數',
                        },
                      ]}
                    >
                      <InputNumber
                        className={styles.inputWidth}
                        placeholder="例如：10（不需輸入單位）"
                        min={1}
                        step={1}
                      />
                    </Item>
                  </Space>
                </Space>

                {isTopUp && (
                  <Space align="baseline" className={styles['ml-3rem']}>
                    <p className={styles['mb-0']}>使用到期日：</p>
                    <Item
                      name="expiration"
                      rules={[{ required: true, message: '請選擇使用到期日' }]}
                    >
                      <DatePicker
                        className={styles.inputWidth}
                        placeholder="請選擇使用到期日"
                      />
                    </Item>
                  </Space>
                )}
              </Space>
            </Radio.Group>
          </Item>
        </Item>

        <Space className={styles.formActionButtons}>
          <Button onClick={closeModal}>取消</Button>
          <Button type="primary" htmlType="submit">
            確定
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};
